import { useEffect, useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Percent, FileDown, RefreshCw, DollarSign } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import TrackTable from '@/components/hr/TrackTable';
import AgentCard from '@/components/hr/AgentCard';
import AgentFormDialog from '@/components/hr/AgentFormDialog';
import {
  downloadTracksPdf,
  generateTracks,
  getAutomationSettings,
  listAutomationHistory,
  markTrackPaid,
  triggerAutomationRun,
  updateAutomationSettings
} from '@/utils/hrApi';
import LogoLoader from '@/components/LogoLoader';
import { formatDateForFilename, formatDisplayDate } from '@/utils/date';

const today = new Date();
const defaultEnd = today.toISOString().slice(0, 10);
const defaultStart = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
  .toISOString()
  .slice(0, 10);

export default function HRCommissions() {
  const { token, user } = useAuth();
  const [hrAgents, setHrAgents] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [tracks, setTracks] = useState([]);
  const [summary, setSummary] = useState({ total_amount: 0, total_hours: 0, agents: 0 });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [activeTrack, setActiveTrack] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [automationSettings, setAutomationSettings] = useState({
    enabled: false,
    cadence_minutes: 1440,
    period_days: 14
  });
  const [emailRecipients, setEmailRecipients] = useState('');
  const [webhookRecipients, setWebhookRecipients] = useState('');
  const [automationHistory, setAutomationHistory] = useState([]);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [triggeringAutomation, setTriggeringAutomation] = useState(false);

  useEffect(() => {
    loadReferenceData(true);
  }, []);

  useEffect(() => {
    if (user?.role === 'Admin') {
      loadAutomationSettings();
    }
  }, [user]);

  const loadReferenceData = async (showOverlay = false) => {
    if (showOverlay) {
      setInitializing(true);
    }
    try {
      const [agents, consultantList] = await Promise.all([
        api.get('/hr-consultants', token),
        api.get('/consultants', token)
      ]);
      setHrAgents(agents);
      setConsultants(consultantList);
    } catch (error) {
      toast.error('Failed to load HR data');
    } finally {
      if (showOverlay) {
        setInitializing(false);
      }
    }
  };

  const filteredTracks = useMemo(() => {
    if (selectedAgent === 'all') {
      return tracks;
    }
    return tracks.filter((track) => track.agent_id === selectedAgent);
  }, [tracks, selectedAgent]);

  const tableRows = useMemo(() => {
    return filteredTracks.flatMap((track) => {
      if (!track.lines || track.lines.length === 0) {
        return [
          {
            trackId: track.id,
            agentName: track.snapshot?.agent_name || 'Unknown agent',
            consultantId: null,
            consultantName: '—',
            hours: 0,
            rate: 0,
            amount: 0,
            status: track.status
          }
        ];
      }
      return track.lines.map((line) => ({
        trackId: track.id,
        agentName: track.snapshot?.agent_name || 'Unknown agent',
        consultantId: line.consultant_id,
        consultantName: line.consultant_name,
        hours: line.hours,
        rate: line.rate,
        amount: line.amount,
        status: track.status
      }));
    });
  }, [filteredTracks]);

  const handleGenerate = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (periodStart > periodEnd) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    try {
      const response = await generateTracks(token, periodStart, periodEnd);
      setTracks(response.tracks || []);
      setSummary(response.summary || { total_amount: 0, total_hours: 0, agents: 0 });
      toast.success('Commission tracks generated');
    } catch (error) {
      const message = error?.response?.data?.detail || 'Unable to generate tracks';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (trackId) => {
    setMarkingId(trackId);
    try {
      const result = await markTrackPaid(token, trackId, {});
      setTracks((prev) => prev.map((item) => (item.id === result.track.id ? result.track : item)));
      toast.success('Track marked as paid and expense recorded');
    } catch (error) {
      const message = error?.response?.data?.detail || 'Failed to mark track as paid';
      toast.error(message);
    } finally {
      setMarkingId(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('Select a period first');
      return;
    }
    setDownloading(true);
    try {
      const blob = await downloadTracksPdf(
        token,
        periodStart,
        periodEnd,
        selectedAgent === 'all' ? null : selectedAgent
      );
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const periodStamp = `${formatDateForFilename(periodStart)}_${formatDateForFilename(periodEnd)}`;
      link.download = `HR_Commissions_${periodStamp}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const unpaidTracks = filteredTracks.filter((track) => track.status === 'unpaid');

  const loadAutomationSettings = async () => {
    setAutomationLoading(true);
    try {
      const settings = await getAutomationSettings(token);
      setAutomationSettings(settings);
      setEmailRecipients((settings.notify_emails || []).join(', '));
      setWebhookRecipients((settings.webhook_urls || []).join('\n'));
      const historyResponse = await listAutomationHistory(token, 15);
      setAutomationHistory(historyResponse.items || []);
    } catch (error) {
      toast.error('Failed to load automation settings');
    } finally {
      setAutomationLoading(false);
    }
  };

  const normaliseList = (value) =>
    value
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);

  const handleSaveAutomation = async () => {
    setSavingAutomation(true);
    try {
      const payload = {
        ...automationSettings,
        cadence_minutes: Number(automationSettings.cadence_minutes) || 1440,
        period_days: Number(automationSettings.period_days) || 14,
        notify_emails: normaliseList(emailRecipients),
        webhook_urls: normaliseList(webhookRecipients)
      };
      const result = await updateAutomationSettings(token, payload);
      setAutomationSettings(result);
      toast.success('Automation settings updated');
      await loadAutomationSettings();
    } catch (error) {
      toast.error('Unable to update automation settings');
    } finally {
      setSavingAutomation(false);
    }
  };

  const handleTriggerAutomation = async () => {
    setTriggeringAutomation(true);
    try {
      const record = await triggerAutomationRun(token);
      if (record?.status === 'skipped') {
        toast.info('Automation is disabled');
      } else {
        toast.success('Automation run triggered');
      }
      await loadAutomationSettings();
    } catch (error) {
      toast.error('Failed to trigger automation run');
    } finally {
      setTriggeringAutomation(false);
    }
  };

  const handleBulkMarkPaid = async () => {
    if (unpaidTracks.length === 0) {
      toast.info('All visible tracks are already settled');
      return;
    }
    setLoading(true);
    for (const track of unpaidTracks) {
      try {
        const result = await markTrackPaid(token, track.id, {});
        setTracks((prev) => prev.map((item) => (item.id === track.id ? result.track : item)));
      } catch (error) {
        toast.error(`Failed to mark ${track.snapshot?.agent_name || 'agent'} as paid`);
        break;
      }
    }
    setLoading(false);
  };

  const consultantOptions = useMemo(() => {
    return consultants.map((consultant) => {
      const assignedAgent = hrAgents.find((agent) => agent.id === consultant.hr_consultant_id);
      return {
        id: consultant.id,
        name: consultant.name,
        email: consultant.email,
        locked: assignedAgent ? assignedAgent.name : null
      };
    });
  }, [consultants, hrAgents]);

  const handleAddAgent = async (form) => {
    setLoading(true);
    try {
      const newAgent = await api.post(
        '/hr-consultants',
        {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          notes: form.notes || null
        },
        token
      );

      if (form.assigned.length > 0) {
        await Promise.all(
          form.assigned.map((consultantId) =>
            api.patch(`/consultants/${consultantId}`, { hr_consultant_id: newAgent.id }, token)
          )
        );
      }

      toast.success('HR agent added');
      setAddDialogOpen(false);
      loadReferenceData();
    } catch (error) {
      toast.error('Failed to add HR agent');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAgent = (agent) => {
    const assigned = consultants
      .filter((consultant) => consultant.hr_consultant_id === agent.id)
      .map((consultant) => consultant.id);
    setEditingAgent({ ...agent, assigned });
    setEditDialogOpen(true);
  };

  const handleUpdateAgent = async (form) => {
    if (!editingAgent) return;
    setLoading(true);
    try {
      await api.patch(
        `/hr-consultants/${editingAgent.id}`,
        {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          notes: form.notes || null
        },
        token
      );

      const currentAssignments = consultants.filter((c) => c.hr_consultant_id === editingAgent.id);
      const removeIds = currentAssignments
        .filter((consultant) => !form.assigned.includes(consultant.id))
        .map((consultant) => consultant.id);
      const addIds = form.assigned.filter(
        (consultantId) => !currentAssignments.some((c) => c.id === consultantId)
      );

      await Promise.all([
        ...removeIds.map((consultantId) =>
          api.patch(`/consultants/${consultantId}`, { hr_consultant_id: null }, token)
        ),
        ...addIds.map((consultantId) =>
          api.patch(`/consultants/${consultantId}`, { hr_consultant_id: editingAgent.id }, token)
        )
      ]);

      toast.success('HR agent updated');
      setEditDialogOpen(false);
      setEditingAgent(null);
      loadReferenceData();
    } catch (error) {
      toast.error('Failed to update HR agent');
    } finally {
      setLoading(false);
    }
  };

  const canManage = user?.role === 'Admin' || user?.role === 'Finance';
  const isAdmin = user?.role === 'Admin';

  const handleViewTrack = (trackId) => {
    const track = tracks.find((item) => item.id === trackId);
    if (track) {
      setActiveTrack(track);
    }
  };

  if (initializing) {
    return <LogoLoader message="Loading HR commission data..." />;
  }

  return (
    <div className="space-y-6">
      {loading && <LogoLoader message="Processing HR commission updates..." fullscreen />}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#3D5B49] flex items-center gap-2">
            <Percent className="h-6 w-6" /> HR Commissions
          </h1>
          <p className="text-muted-foreground">Generate, review, and settle HR agent commission tracks.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleGenerate} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" /> Generate Report
          </Button>
          <Button onClick={handleDownloadPdf} disabled={downloading || tracks.length === 0}>
            <FileDown className="h-4 w-4 mr-2" /> {downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="report" className="space-y-6">
        <TabsList>
          <TabsTrigger value="report">Commission Report</TabsTrigger>
          <TabsTrigger value="agents">HR Agents</TabsTrigger>
          {isAdmin && <TabsTrigger value="automation">Automation</TabsTrigger>}
        </TabsList>

        <TabsContent value="report" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Report Filters</CardTitle>
              <CardDescription>Select the reporting period and optional HR agent filter.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-4">
              <div className="grid gap-2">
                <Label htmlFor="from">From</Label>
                <Input id="from" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="to">To</Label>
                <Input id="to" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>HR Agent</Label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger>
                    <SelectValue placeholder="All agents" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {hrAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2 items-end">
                <Button onClick={handleGenerate} disabled={loading}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {loading ? 'Generating…' : 'Generate'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Total Payable</CardTitle>
                <CardDescription>Sum of commission amounts</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-semibold text-forest">
                ${Number(summary.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Total Hours</CardTitle>
                <CardDescription>Aggregated approved hours</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">
                {Number(summary.total_hours || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}h
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Agents Covered</CardTitle>
                <CardDescription>Unique agents in current view</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">
                {selectedAgent === 'all' ? summary.agents || 0 : filteredTracks.length > 0 ? 1 : 0}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              {filteredTracks.length} track(s) in view — {unpaidTracks.length} unpaid
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handleBulkMarkPaid}
                disabled={!canManage || unpaidTracks.length === 0 || loading}
              >
                <DollarSign className="h-4 w-4 mr-2" /> Mark all paid
              </Button>
            </div>
          </div>

          <TrackTable rows={tableRows} onMarkPaid={handleMarkPaid} onViewTrack={handleViewTrack} marking={markingId} />
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">HR Agent roster</h2>
              <p className="text-muted-foreground text-sm">Manage agents and consultant assignments.</p>
            </div>
            <Button onClick={() => setAddDialogOpen(true)}>Add HR Agent</Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {hrAgents.map((agent) => {
              const assigned = consultants.filter((consultant) => consultant.hr_consultant_id === agent.id);
              return (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  consultantCount={assigned.length}
                  consultants={assigned}
                  onEdit={handleEditAgent}
                />
              );
            })}
            {hrAgents.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  No HR agents found. Add one to begin assigning consultants.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="automation" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Automation cadence</CardTitle>
                <CardDescription>
                  Schedule automatic commission generation and choose who should be notified when runs finish.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label>Enable automation</Label>
                    <p className="text-sm text-muted-foreground">
                      When enabled, the system will periodically generate commission tracks and alert the configured recipients.
                    </p>
                  </div>
                  <Switch
                    checked={automationSettings.enabled}
                    onCheckedChange={(checked) =>
                      setAutomationSettings((prev) => ({ ...prev, enabled: checked }))
                    }
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="grid gap-2">
                    <Label htmlFor="cadence">Cadence (minutes)</Label>
                    <Input
                      id="cadence"
                      type="number"
                      min={15}
                      value={automationSettings.cadence_minutes}
                      onChange={(e) =>
                        setAutomationSettings((prev) => ({
                          ...prev,
                          cadence_minutes: e.target.value
                        }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="period-days">Period length (days)</Label>
                    <Input
                      id="period-days"
                      type="number"
                      min={7}
                      value={automationSettings.period_days}
                      onChange={(e) =>
                        setAutomationSettings((prev) => ({
                          ...prev,
                          period_days: e.target.value
                        }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="updated">Last updated</Label>
                    <Input
                      id="updated"
                      readOnly
                      value={automationSettings.updated_at ? automationSettings.updated_at : 'Not configured'}
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label>Notify emails</Label>
                    <Textarea
                      value={emailRecipients}
                      onChange={(e) => setEmailRecipients(e.target.value)}
                      placeholder="email@example.com, billing@example.com"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">Separate with commas or new lines.</p>
                  </div>
                  <div className="grid gap-2">
                    <Label>Slack/Webhook URLs</Label>
                    <Textarea
                      value={webhookRecipients}
                      onChange={(e) => setWebhookRecipients(e.target.value)}
                      placeholder="https://hooks.slack.com/..."
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">Each webhook is posted when automation finishes.</p>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={handleTriggerAutomation}
                    disabled={!automationSettings.enabled || triggeringAutomation}
                  >
                    {triggeringAutomation ? 'Running…' : 'Run now'}
                  </Button>
                  <Button onClick={handleSaveAutomation} disabled={savingAutomation}>
                    {savingAutomation ? 'Saving…' : 'Save automation'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Automation history</CardTitle>
                <CardDescription>Recent execution outcomes for HR commission automation.</CardDescription>
              </CardHeader>
              <CardContent>
                {automationLoading ? (
                  <LogoLoader message="Loading automation status..." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left">
                          <th className="py-2">Started</th>
                          <th className="py-2">Status</th>
                          <th className="py-2">Period</th>
                          <th className="py-2 text-right">Amount</th>
                          <th className="py-2 text-right">Hours</th>
                          <th className="py-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {automationHistory.map((entry) => (
                          <tr key={entry.id} className="border-t">
                            <td className="py-2">{formatDisplayDate(entry.started_at, ' ')}</td>
                            <td className="py-2 capitalize">{entry.status}</td>
                            <td className="py-2">
                              {formatDisplayDate(entry.period?.start, '-')} — {formatDisplayDate(entry.period?.end, '-')}
                            </td>
                            <td className="py-2 text-right">
                              ${Number(entry.summary?.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 text-right">
                              {Number(entry.summary?.total_hours || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}h
                            </td>
                            <td className="py-2 text-muted-foreground">
                              {entry.error ? entry.error : `${entry.summary?.agents || 0} agent(s)`}
                            </td>
                          </tr>
                        ))}
                        {automationHistory.length === 0 && (
                          <tr>
                            <td className="py-3 text-muted-foreground" colSpan={6}>
                              No automation runs recorded yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <AgentFormDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        title="Add HR Agent"
        submitLabel="Create"
        consultants={consultantOptions}
        onSubmit={handleAddAgent}
      />

      <AgentFormDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditingAgent(null);
        }}
        title="Edit HR Agent"
        submitLabel="Update"
        consultants={consultantOptions}
        initialData={editingAgent}
        onSubmit={handleUpdateAgent}
      />

      <Dialog open={!!activeTrack} onOpenChange={(open) => !open && setActiveTrack(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {activeTrack?.snapshot?.agent_name || 'HR Agent'} — {activeTrack?.status}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Period: {formatDisplayDate(periodStart, '-')} to {formatDisplayDate(periodEnd, '-')}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="py-2">Consultant</th>
                  <th className="py-2 text-right">Hours</th>
                  <th className="py-2 text-right">Rate</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {activeTrack?.lines?.map((line) => (
                  <tr key={line.consultant_id} className="border-t">
                    <td className="py-2">{line.consultant_name}</td>
                    <td className="py-2 text-right">{line.hours.toFixed(2)}</td>
                    <td className="py-2 text-right">${line.rate.toFixed(2)}</td>
                    <td className="py-2 text-right">${line.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
